import React from 'react';
import {StyleSheet, View, type StyleProp, type ViewStyle} from 'react-native';

import {useBreakpoint} from '@/shared/lib/breakpoints';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Числа страницы — ряд плиток показателя.
 *
 * Плитка — `Stat` ui-kit, приборная шкала: цветная риска слева (3pt), подпись 12px
 * приглушённо, крупное моноширинное число, строка «из чего сложилось». Риска
 * повторяет цвет числа: ряд плиток читается по краю, до чтения самих цифр.
 *
 * ## Сетка
 *
 * Плитки лежат в СЕТКЕ с равными ячейками, а не в строке, которая делит ширину по
 * содержимому: сколько бы их ни было — три, четыре, пять, — каждая получает
 * одинаковую ширину, и неполный последний ряд не растягивает свою плитку на всю
 * строку. Колонки: на широком окне — сколько попросили (до пяти), на планшете
 * три, на телефоне две.
 *
 * Цвет тут появляется только у значения и только когда число само по себе —
 * состояние (отказы, просрочки). Остальное — обычная краска текста.
 */
export type StatItem = {
  label: string;
  value: string | number;
  /** Третья строка: из чего сложилось число. */
  hint?: string | null;
  /** Краска значения — и риски слева. Передавать токен палитры, не литерал. */
  tone?: string;
  testID?: string;
};

const styles = StyleSheet.create({
  /* Ячейка сетки платит половину отступа с каждой стороны; сетка компенсирует
     внешнюю половину отрицательным полем. Так плитки в ряду равны по ширине и
     последняя в неполном ряду не растягивается. */
  grid: {flexDirection: 'row', flexWrap: 'wrap', margin: -spacing(1)},
  cell: {padding: spacing(1)},
  tile: {
    flex: 1,
    minWidth: 0,
    gap: 1,
    paddingVertical: spacing(2),
    paddingLeft: spacing(3) - 2,
    paddingRight: spacing(3),
    borderWidth: StyleSheet.hairlineWidth,
    borderLeftWidth: 3,
    borderRadius: radii.md,
    justifyContent: 'center',
  },
});

/** Сколько колонок положено этой ширине окна. */
export function statColumns(
  wanted: number,
  bp: {isTablet: boolean; isDesktop: boolean},
): number {
  const ceiling = bp.isDesktop ? wanted : bp.isTablet ? 3 : 2;
  return Math.max(1, Math.min(wanted, ceiling));
}

export function StatBar({
  items,
  /**
   * Сколько плиток в ряд на широком окне. По умолчанию — все в один ряд (до пяти);
   * на планшете потолок три, на телефоне две.
   */
  columns,
  style,
  testID,
}: {
  items: StatItem[];
  columns?: number;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  const p = useTheme();
  const bp = useBreakpoint();
  const wanted = Math.max(1, columns ?? Math.min(items.length, 5));
  const cols = statColumns(wanted, bp);

  return (
    <View testID={testID} style={[styles.grid, style]}>
      {items.map((item, i) => (
        <View key={`${item.label}-${i}`} style={[styles.cell, {width: `${100 / cols}%`}]}>
          <View
            testID={item.testID}
            style={[
              styles.tile,
              {
                backgroundColor: p.card,
                borderColor: p.border,
                borderLeftColor: item.tone ?? p.borderStrong,
              },
            ]}>
            {/* Подпись переносится, а не обрезается многоточием: «Проходов за
                сег…» не говорит ни о чём. */}
            <AppText variant="eyebrow" numberOfLines={2}>
              {item.label}
            </AppText>
            <AppText variant="metricSm" color={item.tone} numberOfLines={1}>
              {item.value}
            </AppText>
            {item.hint ? (
              <AppText variant="micro" numberOfLines={1}>
                {item.hint}
              </AppText>
            ) : null}
          </View>
        </View>
      ))}
    </View>
  );
}
